import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography
} from '@mui/material';
import {
  examService,
  laboratoryService,
  macroExamService,
  medicalServiceService,
  workstationService
} from 'services/api';
import { GridColDef } from '@mui/x-data-grid-premium';
import { IMacroExam, IMedicalService, IMedicalServiceEtlField, IOption } from 'interfaces';
import { IsTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { loadingActions } from 'features';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CollapsableColumns from 'components/common/CollapsableColumns';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import TenantPage from 'components/common/TenantPage';
import toast from 'features/toast';
import { PurgeButton } from 'components/helpers/PurgeButton';
import LISFileUploader from './LISFileImport';
import ImportFileUploader from 'components/common/file-uploader/ImportFileUploader';
import StyledDropzone from 'components/common/StyledDropzone';
import { medicalServiceEtlFieldService } from 'services/api/MedicalServiceEtlFieldService';
import { isDirector } from 'components/helpers/IsDirector';
import { CreateMedicalService } from '../CreateMedicalService';
import { MedicalServiceColumns } from 'components/common/enhanced/common-headers/medical-service';

interface MedicalServicesProps {
  contextType?: 'STANDARD' | 'OLOS';
}

export const MedicalServices = ({ contextType = 'STANDARD' }: MedicalServicesProps) => {
  const translationPrefix = 'pages.medical-services.list';
  const isDir = isDirector();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  let tenantTitle = t(`${translationPrefix}.title`);

  if (isDir) {
    tenantTitle = tenantTitle.concat(' Emolife');
  }

  const datagridRefresh = useRef(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [openLisDialog, setOpenLisDialog] = useState(false);
  const [openImportLisDialog, setOpenImportLisDialog] = useState(false);
  const [edit, setEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IMedicalService | null>(null);

  const groups = useAppSelector((state) => state.auth.groups);

  const [exams, setExams] = useState<IOption[]>([]);
  const [exam, setExam] = useState<IOption>();
  const [laboratories, setLaboratories] = useState<IOption[]>([]);
  const [lab, setLab] = useState<IOption>();
  const [workstations, setWorkstations] = useState<IOption[]>([]);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [etlHeaderNames, setEtlHeaderNames] = useState<{
    quantity?: string;
    exam_code?: string;
    service_date?: string;
    request_date?: string;
    regional_code?: string;
    req_department?: string;
    req_laboratory?: string;
    exec_laboratory?: string;
    workstation?: string;
  }>({});

  const [macroExams, setMacroExams] = useState<IMacroExam[]>([]);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    if (!isDir) {
      macroExamService
        .getAllPaginated()
        .then((res: { results?: IMacroExam[] }) =>
          setMacroExams(Array.isArray(res?.results) ? res.results : [])
        )
        .catch(() => setMacroExams([]))
        .finally(() => dispatch(loadingActions.stopLoading()));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!exam || !lab) return;
    setWorkstations([]);
    workstationService
      .getAllBaseInformation({ exam: exam.id, laboratory: lab.code })
      .then(setWorkstations);
  }, [exam, lab]);

  useEffect(() => {
    /*NEW LIS - gestione personalizzata delle transocdifiche => VISUALIZZABILI DIRETTAMENTE NELLA TABELLA*/
    dispatch(loadingActions.startLoading());
    examService.getAllBaseInformation().then(setExams);
    laboratoryService.getAllBaseInformation().then(setLaboratories);
    dispatch(loadingActions.stopLoading());
    medicalServiceEtlFieldService.getAllPaginated(1000, 0, { context: contextType }).then((res) => {
      if (res.results.length > 0) {
        setEtlColumnsNames(res.results);
      }
    });
  }, [dispatch, contextType]);

  const setEtlColumnsNames = (etlFields: IMedicalServiceEtlField[]) => {
    setEtlHeaderNames({
      quantity: etlFields.find((etlField) => etlField?.field_to === 'quantity')?.transcode,
      exam_code: etlFields.find((etlField) => etlField?.field_to === 'exam_code')?.transcode,
      service_date: etlFields.find((etlField) => etlField?.field_to === 'service_date')?.transcode,
      request_date: etlFields.find((etlField) => etlField?.field_to === 'request_date')?.transcode,
      regional_code: etlFields.find((etlField) => etlField?.field_to === 'regional_code')
        ?.transcode,
      req_department: etlFields.find((etlField) => etlField?.field_to === 'req_department')
        ?.transcode,
      req_laboratory: etlFields.find((etlField) => etlField?.field_to === 'req_laboratory')
        ?.transcode,
      exec_laboratory: etlFields.find((etlField) => etlField?.field_to === 'exec_laboratory')
        ?.transcode,
      workstation: etlFields.find((etlField) => etlField?.field_to === 'workstation')?.transcode
    });
  };

  const columns: GridColDef[] = MedicalServiceColumns(
    groups,
    macroExams,
    etlHeaderNames.exam_code,
    etlHeaderNames.quantity
  );

  const onOpenDialog = () => {
    setOpenDialog(!openDialog);
    setEdit(false);
  };

  const onOpenLisDialog = () => {
    setOpenLisDialog(!openLisDialog);
  };

  const onOpenImportLisDialog = () => {
    setOpenImportLisDialog(!openImportLisDialog);
  };

  const onSubmitCreate = async (values) => {
    const payload = {
      ...values,
      context: contextType
    };
    medicalServiceService.generateLines(payload).then(() => {
      toast.success('Prestazioni mediche aggiunte.');
      datagridRefresh.current();
    });
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      toast.error(t('global.import-size-error'));
      return;
    }

    const file = acceptedFiles[0];
    if (file) {
      setFileToUpload(file);
    }
  };

  const handleUpload = () => {
    if (fileToUpload) {
      medicalServiceService
        .importAggregatedLinesFromFile(fileToUpload, 'import_file', contextType)
        .then(() => {
          onUploadSuccess();
          toast.success(t('global.import-started'));
          setOpenImportLisDialog(false);
        });
    }
  };

  const handleClose = () => {
    setFileToUpload(null);
    setOpenImportLisDialog(false);
  };

  const onUploadSuccess = () => {
    toast.success('Importazione avviata.');
  };

  return (
    <>
      <TenantPage
        title={tenantTitle}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <div className="flex justify-end items-center">
            <div>
              <ButtonGroup>
                <IsTenantAdministrator>
                  {contextType === 'OLOS' ? (
                    <Button color="success" onClick={onOpenImportLisDialog}>
                      <ReceiptIcon />
                      {t(`${translationPrefix}.import-lis`)}
                    </Button>
                  ) : (
                    <>
                      <Button color="success" onClick={onOpenImportLisDialog}>
                        <ReceiptIcon />
                        {t(`${translationPrefix}.import-lis`)}
                      </Button>
                      <Button onClick={onOpenLisDialog}>
                        <ReceiptIcon />
                        {t(`${translationPrefix}.add-lis`)}
                      </Button>
                      <Button onClick={onOpenDialog}>
                        <AddIcon />
                        {t(`${translationPrefix}.add`)}
                      </Button>
                    </>
                  )}
                </IsTenantAdministrator>
              </ButtonGroup>
            </div>
            <PurgeButton service={medicalServiceService} refresh={datagridRefresh} />
          </div>
        }>
        <CollapsableColumns
          expand={openDialog}
          onClose={() => {
            setSelectedItem(null);
            setOpenDialog(false);
          }}
          size="w-1/3"
          contentLeft={
            <EnhancedDataGrid
              refresh={datagridRefresh}
              service={medicalServiceService}
              columns={columns}
              initialParams={{
                context: contextType
              }}
            />
          }
          contentRight={
            <div className="w-full flex flex-wrap">
              <h2 className="h2 font-bold text-2xl w-full md:w-full text-center  ">
                {edit && selectedItem
                  ? `MODIFICA PRESTAZIONE ${selectedItem.ref}`
                  : 'AGGIUNGI NUOVA PRESTAZIONE'}
              </h2>
              <CreateMedicalService
                translationPrefix={translationPrefix}
                onSubmitCreate={onSubmitCreate}
                exams={exams}
                laboratories={laboratories}
                workstations={workstations}
                setExam={setExam}
                setLab={setLab}></CreateMedicalService>
            </div>
          }
        />
        <LISFileUploader
          openDropzone={openLisDialog}
          setOpenDropzone={setOpenLisDialog}
          onUploadSuccess={onUploadSuccess}
          showButton={false}
          contextType={contextType}
        />
        <Dialog open={openImportLisDialog}>
          <DialogTitle>{t(`import-modal.title`)}</DialogTitle>

          <DialogContent>
            <Typography color="textSecondary">{t(`${translationPrefix}.subtitle`)}</Typography>
            <StyledDropzone onDrop={handleDrop} />
            <div className="mt-4">
              <Link
                underline="hover"
                onClick={() => medicalServiceService.getImportTemplate(contextType)}
                className="cursor-pointer">
                {t('actions.download-template')}
              </Link>
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>{t('actions.cancel')}</Button>
            <Button onClick={handleUpload} disabled={!fileToUpload}>
              {t('actions.upload')}
            </Button>
          </DialogActions>
        </Dialog>
        {openImportLisDialog && (
          <ImportFileUploader service={medicalServiceService} onUploadSuccess={onUploadSuccess} />
        )}
      </TenantPage>
    </>
  );
};

export default MedicalServices;
